import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';
import {gql} from '@apollo/client';
import {apollo} from '../../../utils/apollo';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Loading from '../../../components/Loading';
import PageHeader from '../../../components/PageHeader';
import InformationLine from '../../../components/InformationLine';
import {UsersIcon, ShieldCheckIcon, EnvelopeIcon, GlobeAltIcon, CalendarDaysIcon, CheckCircleIcon, XCircleIcon} from '@heroicons/react/24/outline';

const GET_SUB_USER = gql`
    query GetSubUser($userUuid: String!) {
        getSubUser(userUuid: $userUuid) {
            userUuid
            name
            email
            verified
            active
            createdAt
            country {
                englishName
            }
            roles {
                name
            }
            permissions {
                name
            }
        }
    }
`;

function SubUserView() {
    const {id: userUuid} = useParams();
    const {t} = useTranslation();

    const fetchSubUser = useQuery(
        ['sub_user_view', userUuid],
        async () => {
            const {data} = await apollo.query({
                query: GET_SUB_USER,
                variables: {userUuid},
                fetchPolicy: 'network-only'
            });
            return data?.getSubUser;
        }
    );

    const subUser = useMemo(() => fetchSubUser?.data, [fetchSubUser]);

    return (
        <DashboardLayout>
            <PageHeader
                back="/merchant/sub-users"
                title={t('user_management.view_sub_user')}
                description={t('user_management.view_sub_user_description')}
            />

            {fetchSubUser.isLoading && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center mt-6">
                    <Loading message={t('_.loading')} fullHeight={false} />
                </div>
            )}

            {fetchSubUser.isError && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center mt-6">
                    <p className="text-red-500 font-bold">{t('_.something_went_wrong')}</p>
                </div>
            )}

            {fetchSubUser.isSuccess && !subUser && (
                <div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center mt-6">
                    <p className="text-gray-500 font-bold">{t('_.no_data')}</p>
                </div>
            )}

            {fetchSubUser.isSuccess && subUser && (
                <div className="mt-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Profile Section */}
                        <div className="lg:col-span-1">
                            <div className="bg-white/60 backdrop-blur-md rounded-[2rem] shadow-sm border border-gray-100 p-8 text-center h-full">
                                <div className="mx-auto h-24 w-24 rounded-3xl bg-cyan-50 flex items-center justify-center border border-cyan-100 shadow-sm text-[#0b6a8c] mb-6">
                                    <UsersIcon className="h-12 w-12" />
                                </div>
                                <h3 className="text-2xl font-extrabold text-gray-900">{subUser.name}</h3>
                                <p className="text-gray-500 font-medium mt-1">{subUser.email}</p>
                                
                                <div className="mt-6 flex flex-wrap justify-center gap-2">
                                    {subUser.active ? (
                                        <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 uppercase tracking-wider">
                                            {t('_.active')}
                                        </span>
                                    ) : (
                                        <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-100 uppercase tracking-wider">
                                            {t('_.inactive')}
                                        </span>
                                    )}
                                    {subUser.verified ? (
                                        <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wider">
                                            {t('_.verified')}
                                        </span>
                                    ) : (
                                        <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-100 uppercase tracking-wider">
                                            {t('_.not_verified')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Details Section */}
                        <div className="lg:col-span-2">
                            <div className="bg-white/60 backdrop-blur-md rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden h-full">
                                <div className="px-8 py-6 border-b border-gray-100/50 flex items-center gap-3 bg-white/40">
                                    <ShieldCheckIcon className="h-6 w-6 text-[#0b6a8c]" />
                                    <h3 className="text-xl font-bold text-gray-900">{t('user_management.account_details')}</h3>
                                </div>
                                <div className="p-0">
                                    <InformationLine 
                                        title={t('user_management.table.user_uuid')} 
                                        value={<span className="font-mono text-xs font-bold bg-gray-50 px-2 py-1 rounded">{subUser.userUuid}</span>} 
                                    />
                                    <InformationLine 
                                        title={t('user_management.table.email')} 
                                        value={<div className="flex items-center gap-2"><EnvelopeIcon className="h-4 w-4 text-gray-400" /> {subUser.email}</div>} 
                                    />
                                    <InformationLine 
                                        title={t('user_management.table.country')} 
                                        value={<div className="flex items-center gap-2"><GlobeAltIcon className="h-4 w-4 text-gray-400" /> {subUser.country?.englishName}</div>} 
                                    />
                                    <InformationLine 
                                        title={t('user_management.table.date')} 
                                        value={<div className="flex items-center gap-2"><CalendarDaysIcon className="h-4 w-4 text-gray-400" /> {subUser.createdAt}</div>} 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Roles & Permissions Section */}
                    <div className="bg-white/60 backdrop-blur-md rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-100/50 flex items-center gap-3 bg-white/40">
                            <ShieldCheckIcon className="h-6 w-6 text-[#0b6a8c]" />
                            <h3 className="text-xl font-bold text-gray-900">{t('user_management.roles_permissions')}</h3>
                        </div>
                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">{t('user_management.roles')}</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {subUser.roles?.map((role, idx) => (
                                            <span key={idx} className="px-4 py-2 rounded-xl bg-cyan-50 text-[#0b6a8c] font-bold text-sm border border-cyan-100 shadow-sm">
                                                {role.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">{t('user_management.permissions')}</h4>
                                    <ul className="space-y-4">
                                        <PermissionItem 
                                            active={subUser.permissions?.some(p => p.name === 'sub_user_with_full_merchant_permissions') || false} 
                                            title={t('user_management.full_merchant_permissions')}
                                            description={t('user_management.full_merchant_permissions_desc')}
                                        />
                                        <PermissionItem 
                                            active={subUser.permissions?.some(p => p.name === 'sub_user_with_full_transaction_list') || false}
                                            title={t('user_management.full_transaction_list')}
                                            description={t('user_management.full_transaction_list_desc')}
                                        />
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}

function PermissionItem({active, title, description}) {
    return (
        <li className="flex gap-4 p-4 rounded-2xl bg-gray-50/50 border border-gray-100">
            <div className={`mt-1 flex-shrink-0 ${active ? 'text-emerald-500' : 'text-gray-300'}`}>
                {active ? <CheckCircleIcon className="h-6 w-6" /> : <XCircleIcon className="h-6 w-6" />}
            </div>
            <div>
                <p className="font-bold text-gray-900">{title}</p>
                <p className="text-sm text-gray-500 font-medium">{description}</p>
            </div>
        </li>
    );
}

export default SubUserView;
